import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconCopy } from "@/assets/icons/geist/IconCopy";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { type LogGroups, NO_LOG_GROUP } from "@/routes/environments/log-streams/log-groups";
import { formatError } from "@/utils/errors";
import { type FC, useCallback, useState } from "react";

type CopyAllLogsButtonProps = {
    logGroups?: LogGroups;
    trackingId?: string;
    variant?: "ghost" | "primary" | "secondary" | "destructive";
    size?: "xs" | "sm" | "md" | "lg";
    className?: string;
};

export const CopyAllLogsButton: FC<CopyAllLogsButtonProps> = ({
    logGroups,
    trackingId = "copy-all-logs",
    variant = "ghost",
    size = "sm",
    className,
}) => {
    const { toast } = useToast();
    const [isCopying, setIsCopying] = useState(false);
    const [copied, setCopied] = useState(false);

    const isLegacyRunner =
        logGroups && Object.keys(logGroups).length === 1 && logGroups[NO_LOG_GROUP]?.lines?.length > 0;

    const handleCopyAllLogs = useCallback(async () => {
        if (!logGroups) return;

        setIsCopying(true);
        setCopied(false);

        try {
            const allLogLines: string[] = [];

            if (isLegacyRunner) {
                // For legacy runner, get all lines from NO_LOG_GROUP
                const lines = logGroups[NO_LOG_GROUP]?.lines ?? [];
                for (const line of lines) {
                    if (line.type === "SectionLogLine" || line.type === "LegacyLogLine") {
                        allLogLines.push(line.line);
                    }
                }
            } else {
                // For modern runner, iterate through all groups
                for (const logGroup of Object.values(logGroups)) {
                    if (logGroup.id === NO_LOG_GROUP) continue;

                    // Add group header
                    allLogLines.push(`=== ${logGroup.title} ===`);

                    // Add all lines from the group
                    for (const line of logGroup.lines) {
                        if (line.type === "SectionLogLine" || line.type === "LegacyLogLine") {
                            allLogLines.push(line.line);
                        }
                    }

                    // Add blank line between groups
                    allLogLines.push("");
                }
            }

            const textToCopy = allLogLines.join("\n");
            await navigator.clipboard.writeText(textToCopy);

            setCopied(true);
            toast({ title: "Copied all logs to clipboard" });
        } catch (error) {
            toast({
                title: "Failed to copy logs",
                description: formatError(error),
            });
        } finally {
            setIsCopying(false);
            setTimeout(() => setCopied(false), 2000);
        }
    }, [logGroups, isLegacyRunner, toast]);

    return (
        <Tooltip content="Copy all logs to clipboard" usePortal={true}>
            <Button
                variant={variant}
                size={size}
                className={cn(
                    "border-0",
                    {
                        "disabled:text-content-success": copied,
                    },
                    className,
                )}
                LeadingIcon={copied ? IconCheck : IconCopy}
                disabled={isCopying || copied || !logGroups}
                onClick={handleCopyAllLogs}
                aria-label="Copy all logs"
                data-tracking-id={trackingId}
            >
                <span>{isCopying ? "Copying..." : copied ? "Copied!" : "Copy all logs"}</span>
            </Button>
        </Tooltip>
    );
};
